import React, { useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import SearchIcon from '@material-ui/icons/Search'
import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import TableSortLabel from '@mui/material/TableSortLabel'
import Link from '@mui/material/Link'
import Tooltip from '@mui/material/Tooltip'
import { FormattedMessage, useIntl } from 'react-intl'
import { history } from '../../../_state/helpers'
import { organizationActions } from '../../../_state/actions'
import { CapeeshColors } from '../../../assets/ColorPalette'

const useStyles = makeStyles(() => ({
    root: {
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '40px 40px 60px',
    },
    headerRow: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '16px',
    },
    subHeader: {
        marginTop: '8px',
        color: CapeeshColors.Gray1,
    },
    search: {
        minWidth: '260px',
    },
    tableContainer: {
        marginTop: '28px',
        borderRadius: '12px',
        border: `1px solid ${CapeeshColors.Gray4}`,
    },
    headerCell: {
        fontSize: '13px',
        fontWeight: 500,
        fontFamily: 'Rubik',
        color: CapeeshColors.Gray1,
        whiteSpace: 'nowrap',
    },
    nameLink: {
        color: CapeeshColors.Primary600,
        fontSize: '14px',
        fontFamily: 'Rubik',
        fontWeight: 500,
        textTransform: 'none',
        textAlign: 'left',
    },
    numericCell: {
        fontFamily: 'Rubik',
        fontSize: '14px',
        fontVariantNumeric: 'tabular-nums',
    },
    pendingCell: {
        fontFamily: 'Rubik',
        fontSize: '14px',
        color: CapeeshColors.Gray2,
    },
    totalsRow: {
        fontWeight: 600,
        backgroundColor: CapeeshColors.Gray5,
    },
    emptyCell: {
        padding: '40px',
        color: CapeeshColors.Gray1,
    },
}))

// -1 marks a figure whose per organization stats request has not come back yet. It is not zero,
// and it must not be presented as one - "0 lessons passed" and "not loaded yet" mean very
// different things to someone deciding whether an organization is using Capeesh.
const NOT_LOADED = -1

const COLUMNS = [
    { id: 'name', messageId: 'dashboard.classroom.table.organization', defaultMessage: 'Organization', numeric: false },
    {
        id: 'numberOfStudents',
        messageId: 'dashboard.classroom.table.number.of.students',
        defaultMessage: 'Number of Students',
        numeric: true,
    },
    { id: 'minutesPlayed', messageId: 'dashboard.classroom.table.apptime', defaultMessage: 'App time', numeric: true },
    {
        id: 'numberOfOnboardedUsers',
        messageId: 'dashboard.classroom.table.onboarded.students',
        defaultMessage: 'Onboarded Students',
        numeric: true,
    },
    {
        id: 'numberOfLessonsPassed',
        messageId: 'dashboard.classroom.table.lessons.passed',
        defaultMessage: 'Lessons passed',
        numeric: true,
    },
]

const formatMinutes = (minutes) => {
    if (minutes === NOT_LOADED) {
        return null
    }
    if (!minutes || minutes <= 0) {
        return '0 m'
    }

    const flooredHours = Math.floor(minutes / 60)
    const roundedMinutes = Math.round(minutes - flooredHours * 60)

    if (flooredHours === 0) {
        return `${roundedMinutes} m`
    }

    return `${flooredHours} h ${roundedMinutes} m`
}

const compareRows = (a, b, orderBy) => {
    const left = a[orderBy]
    const right = b[orderBy]

    if (typeof left === 'string' || typeof right === 'string') {
        return (left || '').localeCompare(right || '')
    }

    // Figures that have not loaded sort last whichever way the column points, so sorting by
    // "lessons passed" surfaces real low numbers rather than organizations still loading.
    const leftMissing = left === NOT_LOADED || left === null || left === undefined
    const rightMissing = right === NOT_LOADED || right === null || right === undefined
    if (leftMissing && rightMissing) return 0
    if (leftMissing) return 1
    if (rightMissing) return -1

    return left < right ? -1 : left > right ? 1 : 0
}

/**
 * Organization statistics across every organization the admin can reach, opened from the
 * "Organization statistics" button in the dashboard footer.
 */
const AdministratorReporting = (props) => {
    const classes = useStyles()
    const intl = useIntl()

    const [orderBy, setOrderBy] = useState('name')
    const [order, setOrder] = useState('asc')
    const [searchToken, setSearchToken] = useState('')

    const myOrganizations = props.organization.myOrganizations
    const organizationIdToFactBoxStats = props.organization.organizationIdToFactBoxStats

    // Per organization stats are fetched one request each. This tracks what has already been asked
    // for, so late arriving organizations still get fetched without the list re-requesting
    // everything every time a response lands.
    const requestedOrganizationIds = useRef(new Set())

    useEffect(() => {
        if (!myOrganizations) {
            return
        }

        myOrganizations.forEach((organization) => {
            if (requestedOrganizationIds.current.has(organization.id)) {
                return
            }

            requestedOrganizationIds.current.add(organization.id)
            props.dispatch(organizationActions.getOrganizationFactBoxStats(organization.id))
        })
    }, [myOrganizations])

    const organizationStats = useMemo(() => {
        if (!myOrganizations) {
            return []
        }

        return myOrganizations.map((organization) => {
            const factBox = organizationIdToFactBoxStats?.[organization.id]

            return {
                organizationId: organization.id,
                name: organization.name || '',
                numberOfStudents: organization.usedLicenses ?? NOT_LOADED,
                minutesPlayed: factBox ? factBox.sessionMinutes : NOT_LOADED,
                numberOfOnboardedUsers: factBox ? factBox.numberOfOnboardedUsers : NOT_LOADED,
                numberOfLessonsPassed: factBox ? factBox.numberOfLessonsPassed : NOT_LOADED,
            }
        })
    }, [myOrganizations, organizationIdToFactBoxStats])

    const visibleRows = useMemo(() => {
        const token = searchToken.trim().toLowerCase()
        const filtered = token
            ? organizationStats.filter((row) => row.name.toLowerCase().includes(token))
            : [...organizationStats]

        const direction = order === 'asc' ? 1 : -1
        return filtered.sort((a, b) => direction * compareRows(a, b, orderBy))
    }, [organizationStats, searchToken, order, orderBy])

    const totals = useMemo(
        () =>
            visibleRows.reduce(
                (accumulator, row) => ({
                    numberOfStudents: accumulator.numberOfStudents + Math.max(row.numberOfStudents, 0),
                    minutesPlayed: accumulator.minutesPlayed + Math.max(row.minutesPlayed, 0),
                    numberOfOnboardedUsers:
                        accumulator.numberOfOnboardedUsers + Math.max(row.numberOfOnboardedUsers, 0),
                    numberOfLessonsPassed:
                        accumulator.numberOfLessonsPassed + Math.max(row.numberOfLessonsPassed, 0),
                }),
                {
                    numberOfStudents: 0,
                    minutesPlayed: 0,
                    numberOfOnboardedUsers: 0,
                    numberOfLessonsPassed: 0,
                }
            ),
        [visibleRows]
    )

    const handleSort = (columnId) => {
        if (orderBy === columnId) {
            setOrder(order === 'asc' ? 'desc' : 'asc')
            return
        }

        setOrderBy(columnId)
        // Names read best A to Z, figures read best largest first.
        setOrder(columnId === 'name' ? 'asc' : 'desc')
    }

    // Switches the selected organization and lands on its dashboard. Navigating here rather than
    // through setCurrentOrganization's goToDashboardAfterChange flag, because that flag is only
    // honoured once the saga has finished loading the organization's lessons.
    const handleOpenOrganization = (organizationId) => {
        props.dispatch(organizationActions.setCurrentOrganization(organizationId, false))
        history.push('/dashboard/')
    }

    const renderNumericCell = (value) => {
        if (value === NOT_LOADED) {
            return <span className={classes.pendingCell}>–</span>
        }
        return <span className={classes.numericCell}>{value}</span>
    }

    const renderMinutesCell = (minutes) => {
        const formatted = formatMinutes(minutes)
        if (formatted === null) {
            return <span className={classes.pendingCell}>–</span>
        }
        return <span className={classes.numericCell}>{formatted}</span>
    }

    return (
        <div className={classes.root}>
            <div className={classes.headerRow}>
                <div>
                    <Typography variant="h4">
                        <FormattedMessage id="admin.org.stats.header" defaultMessage="Organization statistics" />
                    </Typography>
                    <Typography variant="body2" className={classes.subHeader}>
                        <FormattedMessage
                            id="admin.org.stats.subHeader"
                            defaultMessage="Here you can see a overall usage of the organizations you have access to"
                        />
                    </Typography>
                </div>

                <TextField
                    className={classes.search}
                    placeholder={intl.formatMessage({
                        id: 'admin.org.stats.search',
                        defaultMessage: 'Search by name',
                    })}
                    value={searchToken}
                    onChange={(event) => setSearchToken(event.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </div>

            <TableContainer className={classes.tableContainer}>
                <Table size="small" aria-label="Organization statistics">
                    <TableHead>
                        <TableRow>
                            {COLUMNS.map((column) => (
                                <TableCell
                                    key={column.id}
                                    align={column.numeric ? 'right' : 'left'}
                                    sortDirection={orderBy === column.id ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === column.id}
                                        direction={orderBy === column.id ? order : 'asc'}
                                        onClick={() => handleSort(column.id)}>
                                        <Typography className={classes.headerCell}>
                                            <FormattedMessage
                                                id={column.messageId}
                                                defaultMessage={column.defaultMessage}
                                            />
                                        </Typography>
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleRows.map((row, index) => (
                            <TableRow
                                key={row.organizationId}
                                hover
                                sx={{
                                    backgroundColor:
                                        index % 2 === 0 ? CapeeshColors.OffWhite : 'transparent',
                                    height: '45px',
                                }}>
                                <TableCell component="th" scope="row">
                                    <Tooltip
                                        title={intl.formatMessage({
                                            id: 'admin.org.stats.switchtoorganization',
                                            defaultMessage: 'Switch to this organization',
                                        })}>
                                        <Link
                                            component="button"
                                            underline="hover"
                                            className={classes.nameLink}
                                            onClick={() => handleOpenOrganization(row.organizationId)}>
                                            {row.name}
                                        </Link>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="right">{renderNumericCell(row.numberOfStudents)}</TableCell>
                                <TableCell align="right">{renderMinutesCell(row.minutesPlayed)}</TableCell>
                                <TableCell align="right">{renderNumericCell(row.numberOfOnboardedUsers)}</TableCell>
                                <TableCell align="right">{renderNumericCell(row.numberOfLessonsPassed)}</TableCell>
                            </TableRow>
                        ))}

                        {visibleRows.length > 0 && (
                            <TableRow className={classes.totalsRow}>
                                <TableCell>
                                    <FormattedMessage
                                        id="admin.org.stats.totals"
                                        defaultMessage="Totals ({count})"
                                        values={{ count: visibleRows.length }}
                                    />
                                </TableCell>
                                <TableCell align="right" className={classes.numericCell}>
                                    {totals.numberOfStudents}
                                </TableCell>
                                <TableCell align="right" className={classes.numericCell}>
                                    {formatMinutes(totals.minutesPlayed)}
                                </TableCell>
                                <TableCell align="right" className={classes.numericCell}>
                                    {totals.numberOfOnboardedUsers}
                                </TableCell>
                                <TableCell align="right" className={classes.numericCell}>
                                    {totals.numberOfLessonsPassed}
                                </TableCell>
                            </TableRow>
                        )}

                        {visibleRows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={COLUMNS.length} align="center" className={classes.emptyCell}>
                                    <FormattedMessage
                                        id="admin.org.stats.noorganizations"
                                        defaultMessage="No organizations match your search."
                                    />
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    )
}

const mapStateToProps = (state) => {
    return {
        organization: state.organization,
    }
}

export default connect(mapStateToProps)(AdministratorReporting)
